import { useState, useCallback } from 'react';
import { useCertLookup } from './useCertLookup';
import { useImportLogs } from '../../../components/BatchCoinForm/hooks/useImportLogs';
import { baseFields } from '../components/BatchLotForm';

const emptyRow = {
  lot_number: '',
  country: '',
  year: '',
  denomination: '',
  variety: '',
  certification_service_id: '',
  certification_number: '',
  pcgs_number: '',
  grade: '',
  cac_sticker: 'none',
  metal: '',
  composition: '',
  weight: '',
  diameter: '',
  edge: '',
  mintage: '',
  population: '',
  pop_higher: '',
  price_guide_value: '',
  assayer: '',
  minor_variety: '',
  die_variety: '',
  reference_number: '',
  estimated_retail_price: '',
  notes: '',
  images: []
};

export const useBatchLotForm = (services) => {
  const [rows, setRows] = useState([{ ...emptyRow }]);
  const [selectedCells, setSelectedCells] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const { logs, addLog, LogViewer } = useImportLogs();
  const { handleFetchCertDetails, fetchProgress } = useCertLookup({ 
    rows, 
    setRows,
    addLog
  });

  const handleAddRow = useCallback(() => {
    setRows(prev => [...prev, { ...emptyRow }]);
  }, []);

  const handleRemoveRow = useCallback((index) => {
    setRows(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleCellChange = useCallback((rowIndex, field, value) => {
    setRows(prev => {
      const newRows = [...prev];
      newRows[rowIndex] = {
        ...newRows[rowIndex],
        [field]: value
      };
      return newRows;
    });
  }, []);

  const handleCellMouseDown = useCallback((e, rowIndex, field) => {
    setSelectedCells([{ rowIndex, field }]);
    setIsDragging(true);
  }, []);

  const handleCellMouseEnter = useCallback((rowIndex, field) => {
    if (isDragging) {
      setSelectedCells(prev => {
        const firstCell = prev[0];
        const cells = [];
        const startRow = Math.min(firstCell.rowIndex, rowIndex);
        const endRow = Math.max(firstCell.rowIndex, rowIndex);
        
        for (let i = startRow; i <= endRow; i++) {
          cells.push({ rowIndex: i, field });
        }
        return cells;
      });
    }
  }, [isDragging]);

  const handleCellMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handlePaste = useCallback((e, rowIndex, field) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text');
    const rows = pasteData.split(/\r\n|\n|\r/).filter(row => row.trim());
    const values = rows[0].split('\t');

    // Find field definition to check type
    const fieldDef = baseFields.find(f => f.key === field);
    const isNumber = fieldDef?.type === 'number';

    const processValue = (value) => {
      if (isNumber) {
        return value.trim() === '' ? null : Number(value);
      }
      return value;
    };

    if (selectedCells.length > 1) {
      // Paste to selected cells
      setRows(prev => {
        const newRows = [...prev];
        selectedCells.forEach((cell, index) => {
          if (index < values.length) {
            const fieldDef = baseFields.find(f => f.key === cell.field);
            const isNumber = fieldDef?.type === 'number';
            newRows[cell.rowIndex] = {
              ...newRows[cell.rowIndex],
              [cell.field]: isNumber ? (values[index].trim() === '' ? null : Number(values[index])) : values[index]
            };
          }
        });
        return newRows;
      });
    } else {
      // Paste to single cell
      setRows(prev => {
        const newRows = [...prev];
        newRows[rowIndex] = {
          ...newRows[rowIndex],
          [field]: processValue(values[0])
        };
        return newRows;
      });
    }
  }, [selectedCells]);

  const handleKeyDown = useCallback((e, rowIndex, fieldIndex, fields) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      
      // If we're on the last row, add a new one
      if (rowIndex === rows.length - 1) {
        handleAddRow();
      }
      
      // Move to the same field in the next row
      const nextRow = document.querySelector(`[data-row="${rowIndex + 1}"][data-field="${fields[fieldIndex].key}"]`);
      if (nextRow) {
        nextRow.focus();
      }
    }
  }, [rows.length, handleAddRow]);

  const cleanRowData = useCallback((row) => {
    const clean = {};
    const requiredFields = ['lot_number', 'country', 'year', 'denomination'];
    const optionalTextFields = [
      'variety', 'certification_service_id', 'certification_number', 
      'pcgs_number', 'grade', 'cac_sticker', 'metal', 'edge', 'mintage', 'assayer',
      'minor_variety', 'die_variety', 'reference_number', 'notes', 'images'
    ];
    const precisionNumberFields = {
      composition: 1, // 1 decimal place for percentage
      weight: 3,     // 3 decimal places for weight in grams
      diameter: 2    // 2 decimal places for diameter in mm
    };
    const integerFields = ['population', 'pop_higher'];
    const priceFields = ['price_guide_value', 'estimated_retail_price'];

    // Handle required fields
    requiredFields.forEach(key => {
      clean[key] = typeof row[key] === 'string' ? row[key].trim() : row[key];
    });

    // Handle optional text fields
    optionalTextFields.forEach(key => {
      const value = row[key];
      if (value !== '' && value !== null) {
        clean[key] = typeof value === 'string' ? value.trim() : value;
      }
    });

    // Handle precision number fields
    Object.entries(precisionNumberFields).forEach(([key, precision]) => {
      const value = row[key];
      if (value === '' || value === null) {
        clean[key] = null;
      } else {
        const parsed = parseFloat(value);
        clean[key] = isNaN(parsed) ? null : Number(parsed.toFixed(precision));
      }
    });

    // Handle integer fields
    integerFields.forEach(key => {
      const value = row[key];
      if (value === '' || value === null) {
        clean[key] = null;
      } else {
        const parsed = parseInt(value);
        clean[key] = isNaN(parsed) ? null : parsed;
      }
    });

    // Handle price fields - default to 0 for empty values
    priceFields.forEach(key => {
      const value = row[key];
      if (value === '' || value === null) {
        clean[key] = 0;
      } else {
        const parsed = parseFloat(value);
        clean[key] = isNaN(parsed) ? 0 : Number(parsed.toFixed(2));
      }
    });

    return clean;
  }, []);

  return {
    rows,
    selectedCells,
    isDragging,
    handleAddRow,
    handleRemoveRow,
    handleCellChange,
    handleCellMouseDown,
    handleCellMouseEnter,
    handleCellMouseUp,
    handlePaste,
    handleKeyDown,
    cleanRowData,
    handleFetchCertDetails,
    fetchProgress,
    logs,
    LogViewer
  };
};
